import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Divider,
  List,
  Row,
  Space,
  Tag,
  Typography,
  notification,
} from "antd";
import { NewCorpEntity } from "./corp-new-entity";
import Context from "@ant-design/icons/lib/components/Context";
import { CorporationOperation } from "../../serviceoperation/CorporationOperation";
import {
  CheckCircleOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  ShopOutlined,
  TeamOutlined,
} from "@ant-design/icons";

interface AddCorpSummaryFormProps {
  newCorpData: NewCorpEntity;
  onCreate: (values: NewCorpEntity, response: unknown) => void;
}

const CorpSummary: React.FC<AddCorpSummaryFormProps> = ({
  newCorpData,
  onCreate,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [api, contextHolder] = notification.useNotification();
  const corpOps = useMemo(() => new CorporationOperation(), []);
  const { Title, Paragraph, Text } = Typography;

  const branchCount = newCorpData.branch?.length ?? 0;
  const departmentCount = (newCorpData.branch ?? []).reduce(
    (total, branch) => total + (branch.departments?.length ?? 0),
    0,
  );

  const companyAddress = newCorpData.address
    ? [
        newCorpData.address.addressLine1,
        newCorpData.address.addressLine2,
        `${newCorpData.address.city}, ${newCorpData.address.state} ${newCorpData.address.zip}`,
        "US",
      ]
        .filter(Boolean)
        .join("\n")
    : "No address provided";

  const processCompanyCreation = async () => {
    if (
      !newCorpData.name ||
      !newCorpData.address ||
      !newCorpData.timeZoneId ||
      !newCorpData.contact?.firstName ||
      !newCorpData.contact?.lastName
    ) {
      api.error({
        message: `Missing required details`,
        description:
          "Please complete required organization and contact details before creating.",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await corpOps.postCorp({
        name: newCorpData.name,
        description: newCorpData.description,
        website: newCorpData.website,
        address: {
          addressLine1: newCorpData.address.addressLine1,
          addressLine2: newCorpData.address.addressLine2,
          addressLine3: undefined,
          city: newCorpData.address.city,
          state: newCorpData.address.state,
          zip: newCorpData.address.zip,
          country: "US",
        },
        timeZoneId: newCorpData.timeZoneId,
        contact: {
          firstName: newCorpData.contact.firstName,
          lastName: newCorpData.contact.lastName,
          email: newCorpData.contact?.email ?? "",
        },
        branch: (newCorpData.branch ?? []).map((x) => ({
          name: x.name,
          branchCode: "str",
          description: x.description,
          address: {
            addressLine1: x.addressLine1,
            addressLine2: x.addressLine2,
            addressLine3: undefined,
            city: x.city,
            state: x.state,
            zip: x.zip,
            country: "US",
          },
          timeZoneId: x.timeZoneId,
          department: (x.departments ?? []).map((dep) => ({
            name: dep.name,
            description: dep.description,
          })),
        })),
        timOffReasons: [
          {
            name: "Personal Time Off",
            description: "Personal time off.",
          },
          {
            name: "Bereavement",
            description:
              "Bereavement leave is paid leave which is available to an employee at the time of death or funeral of a member of the employee's immediate family.",
          },
          {
            name: "Other",
            description: "Other reason that can't be categorized.",
          },
        ],
      });

      if (response.statusCode === 201) {
        api.success({
          message: `Thank you!`,
          description: (
            <Context.Consumer>
              {() =>
                `We've successfully processed your request, please stay on this page while we redirect you!`
              }
            </Context.Consumer>
          ),
        });

        setTimeout(() => {
          onCreate(newCorpData, response);
        }, 5000);
      } else {
        api.error({
          message: `Oops!`,
          description:
            response.statusMessage ||
            "We're sorry for the inconvenience, but we're currently facing some issues processing your request. Please try again later or feel free to contact us for assistance.",
        });
      }
    } catch (error) {
      api.error({
        message: `Oops!`,
        description:
          "Unexpected error while creating organization. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "28px 22px" }}>
      <Card
        style={{
          borderRadius: 18,
          background:
            "linear-gradient(130deg, rgba(255,85,50,0.12), rgba(255,214,163,0.24))",
          border: "1px solid rgba(255,85,50,0.18)",
          marginBottom: 18,
        }}
      >
        <Space direction="vertical" size={6}>
          <Tag
            color="orange"
            style={{
              width: "fit-content",
              borderRadius: 999,
              marginInlineEnd: 0,
            }}
          >
            Final Review
          </Tag>
          <Title level={3} style={{ margin: 0 }}>
            You are almost done!
          </Title>
          <Paragraph style={{ marginBottom: 0, maxWidth: 740 }}>
            Review your setup details below. If everything looks good, create
            your organization and move straight to plan selection.
          </Paragraph>
        </Space>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card style={{ borderRadius: 14 }} bodyStyle={{ padding: 16 }}>
            <Space align="start" size={10}>
              <ShopOutlined style={{ fontSize: 18, color: "#1677ff" }} />
              <Space direction="vertical" size={0}>
                <Text type="secondary">Branches</Text>
                <Title level={4} style={{ margin: 0 }}>
                  {branchCount}
                </Title>
              </Space>
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card style={{ borderRadius: 14 }} bodyStyle={{ padding: 16 }}>
            <Space align="start" size={10}>
              <TeamOutlined style={{ fontSize: 18, color: "#16a34a" }} />
              <Space direction="vertical" size={0}>
                <Text type="secondary">Departments</Text>
                <Title level={4} style={{ margin: 0 }}>
                  {departmentCount}
                </Title>
              </Space>
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card style={{ borderRadius: 14 }} bodyStyle={{ padding: 16 }}>
            <Space align="start" size={10}>
              <GlobalOutlined style={{ fontSize: 18, color: "#7c3aed" }} />
              <Space direction="vertical" size={0}>
                <Text type="secondary">Time Zone</Text>
                <Text strong>{newCorpData.timeZoneId || "-"}</Text>
              </Space>
            </Space>
          </Card>
        </Col>
      </Row>

      <Card style={{ borderRadius: 16, marginBottom: 16 }}>
        <Title level={5}>Corporation</Title>
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12}>
            <Text type="secondary">Company Name</Text>
            <Paragraph style={{ marginBottom: 8 }}>
              {newCorpData.name || "-"}
            </Paragraph>
          </Col>
          <Col xs={24} md={12}>
            <Text type="secondary">Website</Text>
            <Paragraph style={{ marginBottom: 8 }}>
              {newCorpData.website || "-"}
            </Paragraph>
          </Col>
          <Col span={24}>
            <Text type="secondary">Description</Text>
            <Paragraph style={{ marginBottom: 8 }}>
              {newCorpData.description || "-"}
            </Paragraph>
          </Col>
          <Col span={24}>
            <Space align="start" size={8}>
              <EnvironmentOutlined style={{ marginTop: 4, color: "#fa8c16" }} />
              <Space direction="vertical" size={2}>
                <Text type="secondary">Address</Text>
                <Paragraph style={{ whiteSpace: "pre-line", marginBottom: 0 }}>
                  {companyAddress}
                </Paragraph>
              </Space>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card style={{ borderRadius: 16, marginBottom: 16 }}>
        <Title level={5}>Branches and Departments</Title>
        <List
          itemLayout="vertical"
          dataSource={newCorpData.branch ?? []}
          locale={{ emptyText: "No branches configured" }}
          renderItem={(branch) => (
            <List.Item key={branch.name} style={{ paddingInline: 4 }}>
              <Space direction="vertical" size={6} style={{ width: "100%" }}>
                <Space align="center" size={8}>
                  <ShopOutlined style={{ color: "#1677ff" }} />
                  <Text strong>{branch.name}</Text>
                  {branch.timeZoneId && (
                    <Tag color="blue">{branch.timeZoneId}</Tag>
                  )}
                </Space>
                <Paragraph style={{ marginBottom: 0 }}>
                  {branch.description || "No description provided"}
                </Paragraph>
                <Text type="secondary">
                  {branch.addressLine1}
                  {branch.addressLine2 ? `, ${branch.addressLine2}` : ""},{" "}
                  {branch.city}, {branch.state} {branch.zip}, US
                </Text>

                <Space wrap>
                  {(branch.departments ?? []).map((dep) => (
                    <Tag key={`${branch.name}-${dep.name}`} color="geekblue">
                      {dep.name}
                    </Tag>
                  ))}
                  {(branch.departments ?? []).length === 0 && (
                    <Text type="secondary">No departments configured</Text>
                  )}
                </Space>
              </Space>
            </List.Item>
          )}
        />
      </Card>

      <Card style={{ borderRadius: 16 }}>
        <Title level={5}>Default Time Off Reasons</Title>
        <List
          size="small"
          dataSource={[
            {
              name: "Personal Time Off",
              description: "Personal time off.",
            },
            {
              name: "Bereavement",
              description:
                "Bereavement leave is paid leave available at the time of death or funeral of an immediate family member.",
            },
            {
              name: "Other",
              description: "Other reason that cannot be categorized.",
            },
          ]}
          renderItem={(reason) => (
            <List.Item>
              <Space direction="vertical" size={0}>
                <Text strong>{reason.name}</Text>
                <Text type="secondary">{reason.description}</Text>
              </Space>
            </List.Item>
          )}
        />
      </Card>

      <Divider />

      {contextHolder}
      <Space align="center" size={12}>
        <Button
          loading={loading}
          type="primary"
          size="large"
          onClick={processCompanyCreation}
          icon={<CheckCircleOutlined />}
          style={{ borderRadius: 10 }}
        >
          Create Organization
        </Button>
        <Text type="secondary">
          After creation, you will be redirected to select a plan.
        </Text>
      </Space>
    </div>
  );
};

export default CorpSummary;
